import {
  ArrowLeftRight,
  BookUser,
  Building2,
  Contact2,
  Gauge,
  Handshake,
  Home,
  LayoutDashboard,
  LayoutList,
  List,
  Search,
  Send,
  Settings2,
  Shapes,
  TrendingUp,
  Target,
  UserSearch,
} from 'lucide-react';
import { CompanyResearchIcon } from '../components/atoms/icon';
import { getJobsLink } from '../helpers/urlHelpers';
import { useTierGating } from './useTierGating';

export const useMainMenu = (activeNetwork) => {
  const topItems = [];
  const crmItems = [];
  const researchItems = [];
  const researchReportItems = [];
  const jobBoardItems = [];
  const { features } = activeNetwork;

  const { hasCapability, hasAccessToContacts, isCustomer } = useTierGating();
  const isJobBoardEnabled = features && features.includes('job_board');
  const showMovements = hasCapability('movements');

  // Block 1: Top
  if (hasAccessToContacts) {
    topItems.push({
      icon: Home,
      label: 'Home',
      path: `/networks/${activeNetwork.slug}/home`,
    });
  }

  if (hasAccessToContacts) {
    topItems.push({
      icon: TrendingUp,
      label: 'Analytics',
      path: `/networks/${activeNetwork.slug}/analytics`,
    });
  }

  if (activeNetwork.isCompany && activeNetwork.organization) {
    const company = activeNetwork.organization;
    topItems.push({
      icon: LayoutDashboard,
      path: `/networks/${activeNetwork.slug}/company-profile/${company.id}-${company.slug}`,
      label: 'Company Profile',
      tooltip: "Manage your company's profile and jobs on Getro job boards.",
    });
  }

  // Block 2: Main — visible to customers only
  if (isCustomer) {
    crmItems.push({
      icon: Contact2,
      label: 'Contacts',
      path: `/networks/${activeNetwork.slug}/contacts`,
      showUpgradeIcon: !hasCapability('crm_contacts'),
      tooltip: 'View and manage people in your network.',
    });

    crmItems.push({
      icon: List,
      label: 'Lists',
      path: `/networks/${activeNetwork.slug}/lists`,
      showUpgradeIcon: !hasCapability('crm_lists'),
      tooltip: 'Organize contacts and companies for workflows like hiring, sales, and deal sourcing.',
    });

    crmItems.push({
      icon: Shapes,
      label: 'Talent Communities',
      path: `/networks/${activeNetwork.slug}/talent-communities`,
      showUpgradeIcon: !hasCapability('talent_communities'),
      tooltip: 'Build branded communities that grow your talent network.',
    });

    crmItems.push({
      icon: Send,
      label: 'Nurture Campaigns',
      path: `/networks/${activeNetwork.slug}/campaigns`,
      showUpgradeIcon: !hasCapability('campaigns'),
      tooltip: 'Send targeted outreach to contacts and track engagement.',
    });

    researchItems.push({
      icon: UserSearch,
      label: 'People Finder',
      path: `/networks/${activeNetwork.slug}/projects`,
      showUpgradeIcon: !hasCapability('people_finder'),
      tooltip: 'Find the right people beyond your network.',
    });

    researchItems.push({
      icon: CompanyResearchIcon,
      label: 'Company Research',
      path: `/networks/${activeNetwork.slug}/company-research`,
      showUpgradeIcon: !hasCapability('company_research'),
      tooltip: 'Discover companies and find connection paths through your network.',
    });

    if (activeNetwork.isManager && activeNetwork.talentAttractionAvailable) {
      researchReportItems.push({
        icon: Target,
        label: 'Outreach performance',
        path: `/networks/${activeNetwork.slug}/talent-attraction`,
        isBeta: true,
        betaVariant: 'flat-purple',
        tooltip: 'Measure outreach performance and activity for linked jobs.',
      });
    }

    if (showMovements) {
      researchReportItems.push({
        icon: ArrowLeftRight,
        label: 'Movements',
        path: `/networks/${activeNetwork.slug}/movements`,
        isBeta: true,
        betaVariant: 'flat-purple',
        tooltip: 'Track when contacts join, leave, or get promoted at companies in your network. Currently in beta.',
      });
    }

    researchItems.push(
      ...researchReportItems.map((item, index) => (index === 0 ? { ...item, groupLabel: 'Reports' } : item)),
    );

    if (isJobBoardEnabled) {
      jobBoardItems.push({
        icon: Gauge,
        label: 'Dashboard',
        path: `/networks/${activeNetwork.slug}/dashboard`,
      });
      jobBoardItems.push({
        icon: Building2,
        label: 'Companies',
        path: `/networks/${activeNetwork.slug}/companies`,
      });
      jobBoardItems.push({
        icon: LayoutList,
        label: 'Jobs',
        path: getJobsLink({ networkSlug: activeNetwork.slug }),
      });
      jobBoardItems.push({
        icon: Handshake,
        label: 'Hires',
        path: `/networks/${activeNetwork.slug}/hires`,
        tooltip: 'Track when your contacts join companies in your network.',
      });
      jobBoardItems.push({
        icon: Settings2,
        label: 'Settings',
        path: `/networks/${activeNetwork.slug}/settings/job-board`,
      });
    }
  }

  // Block 3: Find candidates — visible to non-customers only
  const findCandidatesItems = !isCustomer
    ? [
        {
          icon: UserSearch,
          label: 'Find candidates',
          path: `/networks/${activeNetwork.slug}/find-candidates`,
        },
      ]
    : [];

  const jobBoardSection = isJobBoardEnabled
    ? { label: 'Job Board', icon: LayoutList, items: jobBoardItems, sectionKey: 'jobBoard' }
    : {
        label: 'JobBoardIntro',
        items: isCustomer
          ? [
              {
                icon: LayoutList,
                label: 'Job Board',
                path: `/networks/${activeNetwork.slug}/job-board-intro`,
                showUpgradeIcon: true,
                tooltip: 'Recruit top talent automatically with an AI-powered job board.',
              },
            ]
          : [],
      };

  const items = [
    { label: 'Top', items: topItems },
    ...(isCustomer
      ? [
          { label: 'CRM', icon: BookUser, items: crmItems, sectionKey: 'crm', separator: true },
          { label: 'Research', icon: Search, items: researchItems, sectionKey: 'research' },
          jobBoardSection,
        ]
      : []),
    ...(findCandidatesItems.length > 0
      ? [{ label: 'Find candidates', items: findCandidatesItems, separator: true }]
      : []),
  ].filter((section) => section.items.length > 0);

  return { items, hasAccessToContacts };
};
